import React, {
  useEffect, useState, useCallback, useContext
} from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import intersection from 'lodash/intersection';
import includes from 'lodash/includes';
import ManageDevicesContext from '../../manage_context';
import { getNodeActions, getActionsForNodeId } from '../../selectors';
import ActionsModal from './manage_modal/actions_modal';
import TransferModal from './manage_modal/transfer_modal';

const manageActions = [
  ['activate', 'deactivate', 'hibernate'],
  ['share', 'lease', 'remove_share', 'remove_lease'],
  ['transfer'],
  ['delete']
];

const ActionsDropdown = ({ handleToggleDropdown }) => {
  const [showHelpText, setShowHelpText] = useState(false);
  const [actionType, setActionType] = useState(null);
  const { selectedDevices } = useContext(ManageDevicesContext);
  const { t } = useTranslation();

  useEffect(() => {
    if (showHelpText && !!selectedDevices.length) {
      setShowHelpText(false);
    }
  }, [showHelpText, selectedDevices]);

  const {
    actions,
    availableActions
  } = useSelector((state) => {
    const nodeActions = getNodeActions(state);
    const grouped = manageActions.map((group) => intersection(group, nodeActions)).filter((group) => !!group.length);
    const available = intersection(
      ...selectedDevices.map((nodeId) => getActionsForNodeId(state, nodeId))
    );
    const all = grouped.map((item, index) => {
      if (index !== grouped.length - 1) {
        return [...item, 'separator'];
      }
      return item;
    });
    return {
      actions: all,
      availableActions: available
    };
  });

  const deselectActionType = useCallback(() => setActionType(null), [setActionType]);

  const isActionAvailable = useCallback((action) => includes(availableActions, action), [availableActions]);

  const handleActionClick = useCallback((action) => () => {
    if (!isActionAvailable(action)) {
      return;
    }
    if (!selectedDevices.length) {
      setShowHelpText(true);
      return;
    }
    setActionType(action);
    handleToggleDropdown();
  }, [handleToggleDropdown, isActionAvailable, selectedDevices, setShowHelpText]);

  return (
    <>
      <button
        type="button"
        key="dropdown"
        className="btn btn-default dropdown-toggle"
        onClick={handleToggleDropdown}
        title={t('thiamis.tooltips.available_actions')}
      >
        <span className="caret-arrow">
          <span className="dropdown-actions">{t('base.labels.actions')}</span>
          <i className="fa" />
        </span>
      </button>
      ,
      <ul key="actions" className="dropdown-menu dropdown-menu-right">
        { _.map(_.flatten(actions), (action, index) => {
          if (action === 'separator') {
            return <li role="separator" className="divider" key={index} />;
          }
          return (
            <li key={index}>
              <a role="button" tabIndex={0} onClick={handleActionClick(action)} disabled={!isActionAvailable(action)}>
                {t(`thiamis.manage_actions.${action}`)}
              </a>
            </li>
          );
        }) }
      </ul>
      { showHelpText && <span className={classNames('help-text')}>{t('thiamis.manage_help_text')}</span> }
      {
        actionType === 'transfer'
          ? <TransferModal type={actionType} toggleModal={deselectActionType} />
          : <ActionsModal type={actionType} toggleModal={deselectActionType} />
      }
    </>
  );
};

export default ActionsDropdown;
